import { useState, useCallback, useEffect, useRef } from 'react';
import { RouteMap } from './components/RouteMap';
import { RouteCard } from './components/RouteCard';
import { ElevationProfile } from './components/ElevationProfile';
import { AddressSearch } from './components/AddressSearch';
import { useRoutePlanner } from './hooks/useRoutePlanner';
import { ROUTE_COLORS, GeocodeResult } from './types/api';

type InputMode = 'origin' | 'destination' | null;
type MobilePanel = 'map' | 'routes';
type ColorBy = 'hills' | 'safety';

function shortAddr(displayName: string): string {
  const parts = displayName.split(',').map((s: string) => s.trim());
  if (parts.length <= 3) return parts.join(', ');
  return parts.slice(0, -2).join(', ');
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return isMobile;
}

function App() {
  const inputModeRef = useRef<InputMode>(null);
  const originRef = useRef<{ lat: number; lon: number } | null>(null);
  const destinationRef = useRef<{ lat: number; lon: number } | null>(null);

  const setInputMode = useCallback((mode: InputMode) => {
    inputModeRef.current = mode;
    _setInputMode(mode);
  }, []);
  const setOrigin = useCallback((loc: { lat: number; lon: number } | null) => {
    originRef.current = loc;
    _setOrigin(loc);
  }, []);
  const setDestination = useCallback((loc: { lat: number; lon: number } | null) => {
    destinationRef.current = loc;
    _setDestination(loc);
  }, []);

  const [origin, _setOrigin] = useState<{ lat: number; lon: number } | null>(null);
  const [destination, _setDestination] = useState<{ lat: number; lon: number } | null>(null);
  const [originText, setOriginText] = useState('');
  const [destText, setDestText] = useState('');
  const [inputMode, _setInputMode] = useState<InputMode>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lon: number } | null>(() => {
    const cached = sessionStorage.getItem('userLocation');
    if (cached) {
      try { return JSON.parse(cached); } catch { /* ignore */ }
    }
    return null;
  });
  const [mapCenter, setMapCenter] = useState<{ lat: number; lon: number } | null>(null);
  const [locRequested, setLocRequested] = useState(false);
  const [mobilePanel, setMobilePanel] = useState<MobilePanel>('routes');
  // Route visualization controls: color the highlighted route by hills or safety,
  // and optionally show the edges the router explored (its "reasoning").
  const [colorBy, setColorBy] = useState<ColorBy>('hills');
  const [showExplored, setShowExplored] = useState(false);

  const isMobile = useIsMobile();

  useEffect(() => {
    if (locRequested || userLocation) return;
    setLocRequested(true);
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setUserLocation(loc);
        sessionStorage.setItem('userLocation', JSON.stringify(loc));
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [locRequested, userLocation]);

  const {
    routes,
    loading,
    precomputing,
    bundleReady,
    error,
    weightOverrides,
    updateWeight,
    planRoutes,
    selectedRoute,
    setSelectedRoute,
  } = useRoutePlanner();

  const handleMapClick = useCallback(
    (lat: number, lon: number) => {
      const mode = inputModeRef.current;
      if (mode === 'origin') {
        setOrigin({ lat, lon });
        setOriginText(`${lat.toFixed(5)}, ${lon.toFixed(5)}`);
        setInputMode(null);
      } else if (mode === 'destination') {
        setDestination({ lat, lon });
        setDestText(`${lat.toFixed(5)}, ${lon.toFixed(5)}`);
        setInputMode(null);
      } else if (!originRef.current) {
        setOrigin({ lat, lon });
        setOriginText(`${lat.toFixed(5)}, ${lon.toFixed(5)}`);
      } else if (!destinationRef.current) {
        setDestination({ lat, lon });
        setDestText(`${lat.toFixed(5)}, ${lon.toFixed(5)}`);
      }
    },
    [setOrigin, setDestination, setInputMode]
  );

  const handleOriginSelect = useCallback((result: GeocodeResult) => {
    setOrigin({ lat: result.lat, lon: result.lon });
    setOriginText(shortAddr(result.display_name));
    setMapCenter({ lat: result.lat, lon: result.lon });
  }, []);

  const handleOriginCoordsChange = useCallback((lat: number, lon: number) => {
    setOrigin({ lat, lon });
    setOriginText(`${lat.toFixed(5)}, ${lon.toFixed(5)}`);
    setMapCenter({ lat, lon });
  }, []);

  const handleDestSelect = useCallback((result: GeocodeResult) => {
    setDestination({ lat: result.lat, lon: result.lon });
    setDestText(shortAddr(result.display_name));
    setMapCenter({ lat: result.lat, lon: result.lon });
  }, []);

  const handleDestCoordsChange = useCallback((lat: number, lon: number) => {
    setDestination({ lat, lon });
    setDestText(`${lat.toFixed(5)}, ${lon.toFixed(5)}`);
    setMapCenter({ lat, lon });
  }, []);

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setOrigin(loc);
        setOriginText(`${loc.lat.toFixed(5)}, ${loc.lon.toFixed(5)}`);
        setUserLocation(loc);
        sessionStorage.setItem('userLocation', JSON.stringify(loc));
        setMapCenter(loc);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const handlePlan = useCallback(() => {
    if (origin && destination) {
      planRoutes(origin, destination, showExplored);
      if (isMobile) setMobilePanel('map');
    }
  }, [origin, destination, planRoutes, isMobile, showExplored]);

  const handleSelectRoute = useCallback((idx: number) => {
    setSelectedRoute(idx);
    if (isMobile) setMobilePanel('map');
  }, [setSelectedRoute, isMobile]);

  const selectedRouteData = routes?.routes[selectedRoute];

  const sidebar = (
    <div className="sidebar">
      <h1>Bike Commute Planner</h1>

      <AddressSearch
        label="Origin"
        value={originText}
        coords={origin}
        onSelect={handleOriginSelect}
        onCoordsChange={handleOriginCoordsChange}
        showLocButton={true}
        onLocClick={handleUseCurrentLocation}
        userLocation={userLocation}
        placeholder="Search address or enter lat, lon"
      />

      <AddressSearch
        label="Destination"
        value={destText}
        coords={destination}
        onSelect={handleDestSelect}
        onCoordsChange={handleDestCoordsChange}
        userLocation={userLocation}
        placeholder="Search address or enter lat, lon"
      />

      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
        <button
          onClick={() => {
            const tmpCoords = origin;
            const tmpText = originText;
            setOrigin(destination);
            setOriginText(destText);
            setDestination(tmpCoords);
            setDestText(tmpText);
          }}
          title="Swap origin and destination"
          className="swap-button"
        >
          ⬆⬇
        </button>
      </div>

      <div style={{ display: 'flex', gap: 4 }}>
        <button
          className="plan-button plan-button-outline"
          style={{ flex: 1, fontSize: 12,
            background: inputMode === 'origin' ? '#e94560' : undefined }}
          onClick={() => setInputMode(inputMode === 'origin' ? null : 'origin')}
        >
          Pick Origin
        </button>
        <button
          className="plan-button plan-button-outline"
          style={{ flex: 1, fontSize: 12,
            background: inputMode === 'destination' ? '#e94560' : undefined }}
          onClick={() => setInputMode(inputMode === 'destination' ? null : 'destination')}
        >
          Pick Dest
        </button>
      </div>

      {inputMode && (
        <div className="input-group" style={{ color: '#e94560', fontSize: 12 }}>
          Tap the map to set {inputMode}
        </div>
      )}

      <details>
        <summary style={{ fontSize: 12, color: '#8888aa', cursor: 'pointer' }}>
          Weight Overrides
        </summary>
        <div style={{ marginTop: 8 }}>
          <SliderControl label="Stress" value={weightOverrides.stress} onChange={(v) => updateWeight('stress', v)} min={0} max={20} step={0.5} />
          <SliderControl label="Climb" value={weightOverrides.climb} onChange={(v) => updateWeight('climb', v)} min={0} max={20} step={0.5} />
          <SliderControl label="Distance" value={weightOverrides.distance} onChange={(v) => updateWeight('distance', v)} min={0} max={10} step={0.5} />
          <SliderControl label="Surface" value={weightOverrides.surface} onChange={(v) => updateWeight('surface', v)} min={0} max={10} step={0.5} />
        </div>
      </details>

      <button
        className="plan-button"
        disabled={!origin || !destination || loading}
        onClick={handlePlan}
      >
        {loading ? 'Planning...' : 'Find Routes'}
      </button>

      {precomputing && (
        <div style={{ fontSize: 11, color: '#88aacc', marginTop: 4 }}>
          Precomputing route bundle for instant slider feedback...
        </div>
      )}
      {bundleReady && !precomputing && (
        <div style={{ fontSize: 11, color: '#88aa88', marginTop: 4 }}>
          Bundle ready &mdash; sliders update the map instantly.
        </div>
      )}

      {routes && (
        <div className="route-cards">
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#8888aa' }}>Highlight color:</span>
            <button
              onClick={() => setColorBy('hills')}
              style={{
                fontSize: 11, padding: '2px 8px', border: 'none', cursor: 'pointer',
                borderRadius: 3,
                background: colorBy === 'hills' ? '#0f3460' : 'transparent',
                color: colorBy === 'hills' ? '#fff' : '#a0a0c0',
              }}
            >
              Hills
            </button>
            <button
              onClick={() => setColorBy('safety')}
              style={{
                fontSize: 11, padding: '2px 8px', border: 'none', cursor: 'pointer',
                borderRadius: 3,
                background: colorBy === 'safety' ? '#0f3460' : 'transparent',
                color: colorBy === 'safety' ? '#fff' : '#a0a0c0',
              }}
            >
              Safety
            </button>
            <label style={{ fontSize: 11, color: '#8888aa', marginLeft: 6, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showExplored}
                onChange={(e) => setShowExplored(e.target.checked)}
                style={{ marginRight: 4 }}
              />
              Show explored
            </label>
          </div>
          <h2>Routes</h2>
          {routes.routes.map((route, idx) => (
            <RouteCard
              key={route.label}
              route={route}
              index={idx}
              selected={idx === selectedRoute}
              onSelect={handleSelectRoute}
            />
          ))}

          {selectedRouteData && (
            <ElevationProfile
              route={selectedRouteData}
              color={ROUTE_COLORS[selectedRouteData.label] || '#e94560'}
            />
          )}
        </div>
      )}
    </div>
  );

  const mapPanel = (
    <RouteMap
      routes={routes}
      selectedRoute={selectedRoute}
      onMapClick={handleMapClick}
      origin={origin}
      destination={destination}
      centerOn={mapCenter}
      colorBy={colorBy}
      showExplored={showExplored}
    />
  );

  if (!isMobile) {
    return (
      <div className="app">
        {sidebar}
        {mapPanel}
      </div>
    );
  }

  return (
    <div className="app mobile">
      <div className={`mobile-panel ${mobilePanel === 'map' ? 'active' : ''}`}>
        {mapPanel}
        {routes && selectedRouteData && mobilePanel === 'map' && (
          <div className="mobile-route-sheet" onClick={() => setMobilePanel('routes')}>
            <div className="sheet-handle" />
            <div className="sheet-content">
              <span className="route-badge" style={{ background: ROUTE_COLORS[selectedRouteData.label] || '#e94560' }} />
              <strong>{selectedRouteData.label}</strong>
              <span className="sheet-stat">{(selectedRouteData.stats.distance_m / 1609.344).toFixed(2)} mi</span>
              <span className="sheet-stat">{(selectedRouteData.stats.elevation_gain_m * 3.28084).toFixed(0)} ft</span>
              <span className="sheet-stat">Safety {selectedRouteData.stats.safety_score.toFixed(0)}</span>
            </div>
          </div>
        )}
      </div>
      <div className={`mobile-panel ${mobilePanel === 'routes' ? 'active' : ''}`}>
        {sidebar}
      </div>
      <div className="mobile-tab-bar">
        <button
          className={`mobile-tab ${mobilePanel === 'map' ? 'active' : ''}`}
          onClick={() => setMobilePanel('map')}
        >
          <span className="tab-icon">🗺</span>
          <span className="tab-label">Map</span>
        </button>
        <button
          className={`mobile-tab ${mobilePanel === 'routes' ? 'active' : ''}`}
          onClick={() => setMobilePanel('routes')}
        >
          <span className="tab-icon">📋</span>
          <span className="tab-label">Routes</span>
        </button>
      </div>
    </div>
  );
}

interface SliderControlProps {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  min: number;
  max: number;
  step: number;
}

function SliderControl({ label, value, onChange, min, max, step }: SliderControlProps) {
  return (
    <div className="slider-group">
      <div className="label-row">
        <span>{label}</span>
        <span>{value !== undefined ? value.toFixed(1) : 'default'}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value ?? min}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          onChange(v);
        }}
      />
    </div>
  );
}

export default App;
